<template>
  <div>
    <input v-if="lineItem.id"
           v-model="lineItem.id"
           type="hidden"
           :name="`invoice[line_items_attributes][${index}][id]`">

    <div v-if="!lineItem.removed" class="line-items-body-item pb-3 pb-lg-0 px-i">
      <div class="line-items-body-item-controls justify-content-between justify-content-lg-center">
        <div class="d-flex d-lg-none"
             :class="isLastItem ? null : 'line-items-body-item-controls-item pointer'"
             @click="isLastItem ? null : removeLineItem(index) ">
          <i v-if="!isLastItem" class="demo-icon icon-times" />
        </div>
        <div v-handle class="line-items-body-item-controls-item grab">
          <i class="demo-icon icon-bars" />
        </div>
        <div class="d-block d-lg-none" />
      </div>
      <div class="line-items-body-item-inputs">
        <input v-model="currency.id"
               type="hidden"
               :name="`invoice[line_items_attributes][${index}][currency_id]`">
        <input v-model="index"
               type="hidden"
               :name="`invoice[line_items_attributes][${index}][sort_index]`">
        <input v-model="taxRate"
               type="hidden"
               :name="`invoice[line_items_attributes][${index}][tax_rate]`">
        <input v-model="taxName"
               type="hidden"
               :name="`invoice[line_items_attributes][${index}][tax_name]`">
        <div class="col-24 col-lg-7 item-cell">
          <label :for="`line-item-name-${index}`" class="d-lg-none">Item</label>
          <input :id="`line-item-name-${index}`"
                 v-model="lineItem.name"
                 required="required"
                 type="text"
                 :name="`invoice[line_items_attributes][${index}][name]`"
                 class="form-control"
                 placeholder="Item name">
        </div>
        <div class="item-cell col-lg-3"
             :class="{ 'col-11': showTaxesDropdown, 'col-24': !showTaxesDropdown }">
          <label :for="`line-item-quantity-${index}`" class="d-lg-none">Quantity</label>
          <input :id="`line-item-quantity-${index}`"
                 v-model="lineItem.quantity"
                 required="required"
                 inputmode="decimal"
                 type="text"
                 :name="`invoice[line_items_attributes][${index}][quantity]`"
                 class="form-control text-center"
                 placeholder="0">
        </div>
        <div v-show="showTaxesDropdown"
             class="col-13 col-lg-4 item-cell">
          <label :for="`line-item-tax-${index}`" class="d-lg-none">Tax</label>
          <select :id="`line-item-tax-${index}`"
                  v-model="lineItem.tax_id"
                  required="required"
                  class="custom-select custom-input"
                  :name="`invoice[line_items_attributes][${index}][tax_id]`">
            <option v-for="tax in taxes" :key="tax.id" :value="tax.id">
              {{ tax.name }} ({{ tax.rate | formatPercentage }})
            </option>
          </select>
        </div>
        <div class="col-24 col-lg-4 item-cell"
             :class="{ 'col-lg-4': showTaxesDropdown, 'col-lg-8': !showTaxesDropdown }">
          <label :for="`line-item-price-${index}`" class="d-lg-none">Price</label>
          <currency-input :id="`line-item-price-${index}`"
                          v-model="lineItem.price"
                          type="text"
                          class="form-control text-center"
                          :currency="null"
                          :distraction-free="false"
                          :placeholder="placeholderPrecision"
                          :precision="currencyPrecision"
                          required="true" />
          <input v-model="lineItem.price"
                 type="hidden"
                 required="true"
                 :name="`invoice[line_items_attributes][${index}][price]`">
        </div>
        <input v-model="taxAmount"
               type="hidden"
               :name="`invoice[line_items_attributes][${index}][tax_amount]`">
        <div class="col-24 col-lg-5 item-cell">
          <label :for="`line-item-amount-${index}`" class="d-lg-none">Amount</label>
          <currency-input :id="`line-item-amount-${index}`"
                          v-model="amount"
                          type="text"
                          class="form-control text-center"
                          :currency="null"
                          :distraction-free="false"
                          :placeholder="placeholderPrecision"
                          :precision="currencyPrecision"
                          disabled />
          <input v-model="amount"
                 type="hidden"
                 class="line-item-amount-js"
                 :name="`invoice[line_items_attributes][${index}][amount]`">
          <input v-model="totalAmount"
                 type="hidden"
                 :name="`invoice[line_items_attributes][${index}][total_amount]`">
        </div>
        <div class="col-1 px-0 item-cell muted d-none d-lg-flex"
             :class="isLastItem ? null : 'line-items-body-item-remove-item'"
             @click="isLastItem ? null : removeLineItem(index) ">
          <i v-if="!isLastItem" class="demo-icon icon-times" />
        </div>
      </div>
    </div>

    <input v-if="lineItem.removed && lineItem.id"
           type="hidden"
           value="true"
           :name="`invoice[line_items_attributes][${index}][_destroy]`">
  </div>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapGetters, mapMutations } from "vuex";
import { ElementMixin, HandleDirective } from "vue-slicksort";

export default {
  directives: { handle: HandleDirective },

  mixins: [ElementMixin],

  props: {
    lineItem: { type: Object }
  },

  computed: {
    ...mapFields("instruments", [
      "taxes"
    ]),

    ...mapGetters("lineItems", {
      currency: "selectedCurrency",
      discountValue: "discountAmount",
      invoiceSubtotal: "subtotalAmount",
      isLastItem: "isOneEmptyLineItemLeft",
      showTaxesDropdown: "showTaxesDropdown"
    }),

    currencyPrecision () {
      if (this.currency === "LBP") {
        return 0;
      } else {
        return 2;
      }
    },

    placeholderPrecision () {
      if (this.currency === "LBP") {
        return "0";
      } else {
        return "0.00";
      }
    },

    amount () {
      return this.lineItem.price * this.lineItem.quantity;
    },

    discountedAmount () {
      if (this.invoiceSubtotal === 0) {
        return 0;
      }

      return Math.max(0, this.amount - this.discountValue * this.amount / this.invoiceSubtotal);
    },

    taxAmount () {
      return this.discountedAmount * this.taxRate / 100;
    },

    totalAmount () {
      return this.discountedAmount + this.taxAmount;
    },

    taxRate () {
      return this.selectedTax.rate;
    },

    taxName () {
      return this.selectedTax.name;
    },

    selectedTax () {
      return this.taxes.find(t => t.id === this.lineItem.tax_id) || { rate: 0 };
    }
  },

  watch: {
    amount (newAmount) {
      this.lineItem.amount = newAmount;
    },

    currency (newCurrency) {
      this.lineItem.currency = newCurrency;
    },

    discountedAmount (newAmount) {
      this.lineItem.discountedAmount = newAmount;
    },

    taxAmount (newAmount) {
      this.taxes.forEach(t => (this.lineItem[`tax_${t.id}_amount`] = 0));

      if (this.selectedTax.id) {
        this.lineItem[`tax_${this.selectedTax.id}_amount`] = newAmount;
        this.lineItem.tax_amount = newAmount;
      }
    },

    totalAmount (newAmount) {
      this.$emit("getPaymentPreview");
      this.lineItem.totalAmount = newAmount;
    },

    selectedTax (newTax) {
      this.lineItem.tax = this.selectedTax;
      this.lineItem.tax_name = newTax.name;
    }
  },

  mounted () {
    this.lineItem.tax = this.selectedTax;
    this.lineItem.currency = this.currency;
  },

  methods: {
    ...mapMutations("lineItems", [
      "removeLineItem"
    ])
  }
};
</script>
